/**
 * ruleInterfaces.ts
 *
 * This file defines TypeScript interfaces / zod validators for the scale rules system, which is used to map
 * input conditions to specific output values in various visualization contexts.
 *
 * Here's a quick summary (may not be exhaustive):
 *
 * - ScaleRuleSet ..... An ordered set of rules that can be applied to a scale (used by GeneralScale).
 *                      Example: { rules: [ScaleRule, ScaleRule, ...] }
 * - ScaleRule ........ A single rule used to map a set of input conditions to a specific output value.
 *                      Example: { outputs: ["#ff0000"], conditions: [{ type: "lt", inputs: [{ value: 20, type: "pn" }] }] }
 * - RuleCondition .... A single condition used within a rule to evaluate whether input(s) match.
 *                      Example: { type: "lt", inputs: [{ value: 20, type: "pn" }] } - for < percentile.
 * - ScaleInput ....... A single input value used in a condition.
 *                      Example: { value: 20, type: "pn" } - for percentile... then from this a `finalValue` is calculated inside.
 * - ScaleOutput ...... Represents output value(s) used in a rule - such as a constant color/size, or a gradient, color cycle, etc.
 *                      Example: { outputValues: ["#ff0000"], outputType: "constant" }
 */

import { typed } from "@/components/ec/engine/surfaceFramework/definition/atomDeclarations";
import type { ColumnStats } from "@/utilities/column/stats/statsInterfaces";
import type { ScaleRuleLabelDisplay } from "@/utilities/scale/rules/ruleConstants";
import {
  D3ScaleType,
  RuleConditionOperator,
  ScaleConditionType,
  ScaleInputValueType,
  ScaleOutputValueType,
} from "@/utilities/scale/rules/ruleConstants";
import { AggregationMode } from "@/endlaFunctions/customTypings/aggregationTypes";
import { z } from "zod";

/**
 * Represents a single input value used in a condition.
 *
 * Note:
 * - This has its own interface because we allow for different "types" of values:
 *   "frac" (fraction between min/max range), "pn" (percentile), "bottom_x", "top_x", etc.
 * - Most value types are only resolved to an exact/final value when we evaluate
 *   the array of domain values against the input condition.
 */
export interface ScaleInput {
  /**
   * The raw/relative input "value" (e.g., 0.4 - as a "fract" type).
   *
   * For certain input types like 'BaseEncodingMin' this can be null, and
   * only the `finalValue` gets set when the domain is resolved.
   */
  // oxlint-disable-next-line typescript/no-explicit-any
  value: any;

  /**
   * The type of the `input`. Examples: "abs", "frac", "pn", "bottom_x", "top_x", ...
   *
   * Default is "abs" (final value); most others (like pn = percentile) resolve into `finalValue`.
   */
  type?: ScaleInputValueType;
}

export const scaleInputSchema = typed<ScaleInput>()(
  z.object({
    value: z.unknown(),
    // oxlint-disable-next-line typescript-eslint/no-deprecated
    type: z.nativeEnum(ScaleInputValueType).optional(),
  })
);

/**
 * A single condition used within a rule to evaluate whether input(s) match.
 *
 * Example for "< 20%" comparison:
 * ```
 * { type: "lt", inputValues: [{ value: 20, type: "pn" }] }
 * ```
 */
export const ruleConditionSchema = z.object({
  /**
   * Type of comparison to evaluate.
   * Examples: "lt" (less than), "gt", "between", "eq", "empty", "contains", "starts_with", etc.
   */
  // oxlint-disable-next-line typescript-eslint/no-deprecated
  type: z.nativeEnum(ScaleConditionType),
  /**
   * The user-provided input / domain / threshold value(s) to compare against. Each one can be a
   * different type — so e.g. a "between" condition can combine a number and a percentile.
   *
   * TODO(noske): Rename this to just `input` (talk to Riley — may break stored presets).
   */
  inputValues: z.array(scaleInputSchema).optional(),
  /** If true, the logic of this condition is inverted (i.e. "not"). */
  inverted: z.boolean().optional(),
  /**
   * Optional operator to combine multiple conditions together ("and"/"or").
   *
   * The first rule condition is always empty. For subsequent conditions, empty is treated as "and".
   */
  // oxlint-disable-next-line typescript-eslint/no-deprecated
  operator: z.nativeEnum(RuleConditionOperator).optional(),
});

export type RuleCondition = z.infer<typeof ruleConditionSchema>;

export type FinalizedScaleInput = ScaleInput & { finalValue?: ScaleInput["value"] };

export type FinalizedRuleCondition = Omit<RuleCondition, "inputValues"> & {
  inputValues?: FinalizedScaleInput[];
};

/**
 * Gradient scale configuration options, used when `scale.output.outputType === "gradient"`.
 */
export const scaleGradientConfigSchema = z.object({
  /**
   * Fractional values in [0, 1] defining the positions of the inputs in the gradient.
   *
   * For example `[0, 0.5, 1]` creates a gradient with three stops. Defaults to `[0, 1]`;
   * its length should match the number of outputs.
   */
  gradientStopFraction: z.array(z.number()).optional(),
  /** Number of steps over the gradient. Zero → continuous (no steps). */
  numGradientSteps: z.number().optional(),
});

export type ScaleGradientConfig = z.infer<typeof scaleGradientConfigSchema>;

export const FINAL_D3_SCALE_FN = Symbol("__FINAL_D3_SCALE_FN__");

/** D3 scale configuration options, used when `scale.output.outputType === "d3_scale"`. */
export const scaleD3ConfigSchema = z.object({
  /** One of "linear", "quantile", ... — drives `d3Scale` compilation. */
  // oxlint-disable-next-line typescript-eslint/no-deprecated
  d3ScaleType: z.nativeEnum(D3ScaleType),
  /** Number of steps to apply to the ramp, if applicable. Required for quantile scales. */
  rampSteps: z.number().optional(),
  /** Reverses the ramp. */
  rampReverse: z.boolean().optional(),
  /**
   * Scheme name for color ramps, e.g. "interpolateRainbow".
   *
   * @deprecated Slated for removal — the same value lives in `outputValues`.
   */
  name: z.string().optional(),
});

/**
 * See {@link scaleD3ConfigSchema}. `[FINAL_D3_SCALE_FN]` is a runtime-only
 * compiled function; it is not part of the schema and never persisted.
 */
export type ScaleD3Config = z.infer<typeof scaleD3ConfigSchema> & {
  // oxlint-disable-next-line typescript/no-explicit-any
  [FINAL_D3_SCALE_FN]?: any;
};

/**
 * Output value(s) used in a rule — constant, gradient, cycle, d3_scale.
 * */
export interface ScaleOutput {
  /**
   * The values of the output. Examples:
   * - `["#ff0000"]` for a constant color, `["interpolateRainbow"]` for a color ramp.
   * - `[20]` for a constant size.
   * - `["circle"]` for a constant shape (one of `ShapeType` / `SHAPES_2D`).
   *
   * Or two or more values for a "gradient" or a "cycle". Element type depends on dimension.
   */
  // TODO(noske): Might make this non-optional (will have to update all tests, etc.).
  // oxlint-disable-next-line typescript/no-explicit-any
  outputValues: any[];

  /**
   * The output type: "constant" | "gradient" | "cycle" | "d3_scale".
   *
   * If missing, default is "constant".
   */
  // TODO(noske): Finish making this non-optional (#13191).
  outputType: ScaleOutputValueType;

  /** Additional options when `outputType === "gradient"`. */
  scaleGradientConfig?: ScaleGradientConfig;

  /** Additional options when `outputType === "d3_scale"` (e.g. color ramp). */
  scaleD3Config?: ScaleD3Config;
}

export const scaleOutputSchema = typed<ScaleOutput>()(
  z.object({
    outputValues: z.array(z.unknown()),
    // oxlint-disable-next-line typescript-eslint/no-deprecated
    outputType: z.nativeEnum(ScaleOutputValueType),
    scaleGradientConfig: scaleGradientConfigSchema.optional(),
    scaleD3Config: scaleD3ConfigSchema.optional(),
  })
);

/**
 * A single rule mapping a set of input conditions to a specific output value.
 * */
export const scaleRuleSchema = z.object({
  /**
   * Unique identifier for the rule, used to track changes and updates.
   * Not persisted — recreated on initialization.
   */
  uuid: z.string().optional(),
  /**
   * Output / range value(s) associated with this rule. Returned when conditions match.
   *
   * Typically one value; may be multiple for gradients / cycles.
   */
  output: scaleOutputSchema,
  /** Input conditions that must all be satisfied (in order) for the rule to apply. */
  conditions: z.array(ruleConditionSchema),
  /** Whether the rule is disabled (temporarily turned off) without being deleted. */
  disabled: z.boolean().optional(),
});

export type ScaleRule = z.infer<typeof scaleRuleSchema>;

export type FinalizedScaleRule = Omit<ScaleRule, "conditions"> & {
  conditions: FinalizedRuleCondition[];
};

/**
 * A wrapper around a set of rules that can be applied to a scale.
 * */
export const scaleRuleSetSchema = z.object({
  /** Rules in order of precedence. The first rule that matches wins. */
  rules: z.array(scaleRuleSchema).min(1),
  /**
   * Name for the rule set — mostly used in suggested-rule-sets previews.
   * TODO: drop this; user-editable labels are already on the preset.
   */
  name: z.string().optional(),
  /**
   * Minimum / maximum values for the domain, using the chosen `aggregationMode`.
   *
   * Default to the domain min/max but can be user-overridden. Rule condition inputs
   * can reference them via `ScaleInputValueType.BaseMin` / `BaseMax`.
   */
  baseMin: scaleInputSchema,
  baseMax: scaleInputSchema,
  /** How labels render (value_first / value_last / short / relative_edit). Default "value_first". */
  labelFormat: z.custom<ScaleRuleLabelDisplay>().optional(),
});
export type ScaleRuleSet = z.infer<typeof scaleRuleSetSchema>;

export type FinalizedScaleRuleSet = Omit<ScaleRuleSet, "baseMin" | "baseMax" | "rules"> & {
  baseMin: FinalizedScaleInput;
  baseMax: FinalizedScaleInput;
  rules: FinalizedScaleRule[];
  columnStats?: ColumnStats;
};

// ---------------------------------------------------------------------------
// Leaf schemas for common enum / literal unions (no accompanying TS type —
// the enum/union is defined elsewhere and imported above).
// ---------------------------------------------------------------------------

// oxlint-disable-next-line typescript-eslint/no-deprecated
export const aggregationModeSchema = z.nativeEnum(AggregationMode);

export const scaleDataStateSchema = z.union([
  z.literal("all"),
  z.literal("filters"),
  z.literal("selection"),
  z.literal("focus"),
]);
